import type { Ref } from 'vue'

// 全局 Vditor 实例缓存
let VditorClass: any = null
let isVditorLoading = false
let vditorLoadPromise: Promise<any> | null = null

// 预加载 Vditor
const preloadVditor = async (): Promise<any> => {
  if (VditorClass) return VditorClass
  
  if (isVditorLoading && vditorLoadPromise) {
    return vditorLoadPromise
  }
  
  isVditorLoading = true
  vditorLoadPromise = import('vditor').then(module => {
    VditorClass = module.default
    isVditorLoading = false
    return VditorClass
  }).catch(error => {
    isVditorLoading = false
    vditorLoadPromise = null
    throw error
  })
  
  return vditorLoadPromise
}

export const useVditor = () => {
  // 创建编辑器实例
  const createEditor = async (
    container: HTMLElement,
    options: {
      value?: string
      placeholder?: string
      height?: number
      mode?: 'wysiwyg' | 'ir' | 'sv'
      onChange?: (value: string) => void
    } = {}
  ) => {
    try {
      const Vditor = await preloadVditor()
      
      return new Vditor(container, {
        height: options.height || 400,
        mode: options.mode || 'wysiwyg',
        placeholder: options.placeholder || '请输入内容...',
        value: options.value || '',
        cache: {
          enable: false
        },
        toolbar: [
          'emoji',
          'headings',
          'bold',
          'italic',
          'strike',
          '|',
          'line',
          'quote',
          'list',
          'ordered-list',
          'check',
          '|',
          'code',
          'inline-code',
          'link',
          'table',
          '|',
          'undo',
          'redo',
          '|',
          'preview',
          'fullscreen'
        ],
        input: options.onChange,
        preview: {
          hljs: {
            enable: true,
            style: 'github'
          },
          math: {
            inlineDigit: true
          }
        }
      })
    } catch (error) {
      console.error('Failed to create Vditor editor:', error)
      return null
    }
  }

  // 优化的预览渲染函数
  const renderPreview = async (container: HTMLElement, content: string): Promise<void> => {
    try {
      if (!content || !container) {
        return
      }

      const Vditor = await preloadVditor()

      // 清空容器
      container.innerHTML = ''

      // 使用 Vditor 的静态预览功能直接渲染到DOM
      Vditor.preview(container, content, {
        mode: 'light',
        anchor: 0,
        math: {
          inlineDigit: true
        },
        hljs: {
          enable: true,
          style: 'github'
        },
        after: () => {
          // 渲染完成后添加代码复制功能
          addCodeCopyFunction()
        }
      })
    } catch (error) {
      console.error('渲染错误:', error)
      // 如果渲染失败，显示原始文本
      if (container) {
        container.innerHTML = content.replace(/\n/g, '<br>')
      }
    }
  }

  // HTML 转义函数
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // 添加代码复制功能到页面
  const addCodeCopyFunction = () => {
    if (typeof window !== 'undefined' && !(window as any).copyCode) {
      (window as any).copyCode = (button: HTMLButtonElement) => {
        const pre = button.closest('pre')
        const code = pre?.querySelector('code')
        if (code) {
          navigator.clipboard.writeText(code.textContent || '').then(() => {
            button.textContent = '已复制'
            setTimeout(() => {
              button.textContent = '复制'
            }, 2000)
          }).catch(() => {
            // 备用复制方法
            const textArea = document.createElement('textarea')
            textArea.value = code.textContent || ''
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            button.textContent = '已复制'
            setTimeout(() => {
              button.textContent = '复制'
            }, 2000)
          })
        }
      }
    }
  }

  // 预加载函数，供外部调用
  const warmup = () => {
    if (process.client) {
      preloadVditor()
    }
  }

  return {
    createEditor,
    renderPreview,
    addCodeCopyFunction,
    escapeHtml,
    warmup,
    preloadVditor
  }
}